import { useEffect, useRef, useState, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type DayPlanKitFlag,
  type LabKitExpirationDto,
  PATIENT_VISIT_FLAGS,
  type SaveVisitKitsRequest,
} from '@ctms/contracts';
import {
  AlertTriangle,
  ArrowRight,
  Check,
  Loader2,
  Package,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react';

import { patientQueryKeys } from '../../patients/patients.query-keys.js';
import { visitQueryKeys } from '../../visits/visits.query-keys.js';
import { kitsApi } from '../kits.api.js';
import { kitQueryKeys } from '../kits.query-keys.js';

export interface PredictedKitItem {
  id: number;
  labKitId: number;
  name: string;
  count: number;
  notes?: string | null;
  expiration: boolean;
}

export interface UsedKitItem {
  id: string; // row unique uuid
  labKitId: number;
  name: string;
  expiration: boolean;
  expirationDate: string | null;
  status: 'przygotowany' | 'zużyty';
}

export interface KitRegistrationProps {
  patientVisitId: number;
  trialId: number;
  visitLabel: string;
  patientName?: string;
  patientCode?: string;
  trialShortcut?: string;
  initialFlag?: DayPlanKitFlag | null;
  canRead: boolean;
  canWrite: boolean;
  onSaved?: (newFlag: DayPlanKitFlag, usedKits: UsedKitItem[]) => void;
  onOpenInventory?: () => void;
  onClose: () => void;
}

export const KIT_FLAG_LABELS: Record<DayPlanKitFlag, string> = {
  W: 'Biała — brak użycia',
  R: 'Czerwona — proces niepełny',
  G: 'Zielona — komplet',
};

export const KIT_FLAG_COLORS: Record<DayPlanKitFlag, { bg: string; text: string; border: string }> = {
  W: { bg: 'bg-slate-800', text: 'text-slate-200', border: 'border-slate-600' },
  R: { bg: 'bg-rose-950/80', text: 'text-rose-300', border: 'border-rose-700' },
  G: { bg: 'bg-emerald-950/80', text: 'text-emerald-300', border: 'border-emerald-700' },
};

function todayIso(): string {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${m}-${day}`;
}

export function isDateExpired(dateStr: string): boolean {
  return dateStr < todayIso();
}

/** Default mock predicted kits matching spec/domains/lab-kits/ui/kit-registration.ui.md */
const DEFAULT_PREDICTED_KITS: PredictedKitItem[] = [
  { id: 1, labKitId: 101, name: 'Screening blood kit', count: 1, notes: null, expiration: true },
  { id: 2, labKitId: 102, name: 'Urine cup', count: 1, notes: null, expiration: false },
  { id: 3, labKitId: 103, name: 'Stool kit (PG)', count: 1, notes: 'tylko substudy PG', expiration: true },
  { id: 4, labKitId: 104, name: 'HBV-DNA kit', count: 1, notes: 'warunkowo HBV(+)', expiration: true },
];

export function KitRegistrationDialog({
  patientVisitId,
  trialId,
  visitLabel,
  patientName = 'Anna Przykładowa',
  patientCode = 'SCR-2401',
  trialShortcut = 'DEMO-LAB-02',
  initialFlag = 'R',
  canRead,
  canWrite,
  onSaved,
  onOpenInventory,
  onClose,
}: KitRegistrationProps): JSX.Element {
  // Source selector: 'visit' = Tylko ta wizyta, 'trial' = Wszystkie kity badania (REQ-KIT-020)
  const [source, setSource] = useState<'visit' | 'trial'>('visit');
  const [flag, setFlag] = useState<DayPlanKitFlag>(initialFlag ?? PATIENT_VISIT_FLAGS.RED);

  // Left panel: predicted / available kits
  const [predictedKits, setPredictedKits] = useState<PredictedKitItem[]>(
    trialId > 0 ? [] : DEFAULT_PREDICTED_KITS,
  );
  const hasSeededRef = useRef(false);

  // Right panel: used / prepared kits (REQ-KIT-017)
  const [usedKits, setUsedKits] = useState<UsedKitItem[]>([]);

  // Batch expirations cache per labKitId
  const [expirationsCache, setExpirationsCache] = useState<Record<number, LabKitExpirationDto[]>>({});

  // Pending removals / expired confirmations
  const [removalTarget, setRemovalTarget] = useState<UsedKitItem | null>(null);
  const [expiredWarning, setExpiredWarning] = useState<{ item: UsedKitItem; date: string } | null>(null);
  const [infoModal, setInfoModal] = useState<string | null>(null);

  // Fetch laboratories of the trial
  const laboratoriesQuery = useQuery({
    queryKey: kitQueryKeys.laboratories.byTrial(trialId),
    queryFn: () => kitsApi.listLaboratories({ trialId, page: 1, limit: 100 }),
    enabled: canRead && trialId > 0,
    staleTime: 5 * 60 * 1000,
  });

  const firstLabId = laboratoriesQuery.data?.items[0]?.id;

  // Fetch kits of the first laboratory
  const trialKitsQuery = useQuery({
    queryKey: kitQueryKeys.kits.byLab(firstLabId ?? 0),
    queryFn: () => kitsApi.listKits({ trialLaboratoryId: firstLabId ?? 0, page: 1, limit: 100 }),
    enabled: canRead && Boolean(firstLabId),
    staleTime: 5 * 60 * 1000,
  });

  // Seed predicted kits from trial kits if available
  useEffect(() => {
    if (!hasSeededRef.current && trialKitsQuery.data?.items && trialKitsQuery.data.items.length > 0) {
      hasSeededRef.current = true;
      const loadedKits: PredictedKitItem[] = trialKitsQuery.data.items.map((k) => ({
        id: k.id,
        labKitId: k.id,
        name: k.name,
        count: 1,
        notes: k.notes,
        expiration: k.expiration,
      }));
      setPredictedKits(loadedKits);
    }
  }, [trialKitsQuery.data?.items]);

  // Kits already registered at this visit (REQ-KIT-013..022)
  const visitKitsQuery = useQuery({
    queryKey: visitQueryKeys.visitKits(patientVisitId),
    queryFn: () => kitsApi.getVisitKits(patientVisitId),
    enabled: canRead && patientVisitId > 0,
    staleTime: 0,
  });
  const hasHydratedRef = useRef(false);

  // Hydrate the right panel from the saved rows once catalogue + saved kits are known
  useEffect(() => {
    const saved = visitKitsQuery.data;
    const catalogue = trialKitsQuery.data?.items;
    if (hasHydratedRef.current || !saved || !hasSeededRef.current || !catalogue) {
      return;
    }
    hasHydratedRef.current = true;
    if (saved.flag) {
      setFlag(saved.flag);
    }
    if (saved.items.length === 0) {
      return;
    }
    void (async () => {
      const rows: UsedKitItem[] = [];
      for (const row of saved.items) {
        const kit = catalogue.find((k) => k.id === row.labKitId);
        if (!kit) {
          continue;
        }
        let expirationDate: string | null = null;
        if (row.labKitExpirationId !== null) {
          const batches = await loadExpirationsRef.current(row.labKitId);
          expirationDate =
            batches.find((b) => b.id === row.labKitExpirationId)?.expirationDate ?? null;
        }
        rows.push({
          id: `saved-${row.id}`,
          labKitId: row.labKitId,
          name: kit.name,
          expiration: kit.expiration,
          expirationDate,
          status: row.status,
        });
      }
      setUsedKits(rows);
      setPredictedKits((prev) => prev.filter((p) => !rows.some((r) => r.labKitId === p.labKitId)));
    })();
  }, [visitKitsQuery.data, trialKitsQuery.data?.items]);

  // Load batch expirations for date-bearing kits
  const loadExpirations = async (kitId: number): Promise<LabKitExpirationDto[]> => {
    if (expirationsCache[kitId]) {
      return expirationsCache[kitId];
    }
    try {
      const expList = await kitsApi.listExpirations(kitId);
      // Sort FEFO (earliest expiry first)
      const sorted = [...expList].sort((a, b) => a.expirationDate.localeCompare(b.expirationDate));
      setExpirationsCache((prev) => ({ ...prev, [kitId]: sorted }));
      return sorted;
    } catch {
      return [];
    }
  };
  const loadExpirationsRef = useRef(loadExpirations);
  loadExpirationsRef.current = loadExpirations;

  // Move one kit from predicted to used (REQ-KIT-017)
  const handleMoveToUsed = async (kit: PredictedKitItem): Promise<void> => {
    let chosenExpiry: string | null = null;

    if (!kit.expiration) {
      chosenExpiry = null;
    } else {
      const expList = await loadExpirations(kit.labKitId);
      if (expList.length > 0) {
        // Default to earliest date (FEFO suggestion, REQ-KIT-019)
        chosenExpiry = expList[0]?.expirationDate ?? null;
      }
    }

    const newUsed: UsedKitItem = {
      id: `${kit.labKitId}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      labKitId: kit.labKitId,
      name: kit.name,
      expiration: kit.expiration,
      expirationDate: chosenExpiry,
      status: 'przygotowany',
    };

    setUsedKits((prev) => [...prev, newUsed]);
    // Remove from predicted list
    setPredictedKits((prev) => prev.filter((p) => p.id !== kit.id));
  };

  // Move all predicted kits to used (REQ-KIT-017, #move-all-to-used)
  const handleMoveAllToUsed = async (): Promise<void> => {
    const newlyUsed: UsedKitItem[] = [];

    for (const kit of predictedKits) {
      let chosenExpiry: string | null = null;
      if (kit.expiration) {
        const expList = await loadExpirations(kit.labKitId);
        if (expList.length > 0) {
          chosenExpiry = expList[0]?.expirationDate ?? null;
        }
      }
      newlyUsed.push({
        id: `${kit.labKitId}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        labKitId: kit.labKitId,
        name: kit.name,
        expiration: kit.expiration,
        expirationDate: chosenExpiry,
        status: 'przygotowany',
      });
    }

    setUsedKits((prev) => [...prev, ...newlyUsed]);
    setPredictedKits([]);
  };

  // Prompt removal of used kit (REQ-KIT-015)
  const handleRequestRemoveUsed = (item: UsedKitItem): void => {
    if (item.status === 'przygotowany') {
      setRemovalTarget(item);
    } else {
      // Already physically used, remove directly or prompt
      confirmRemoveUsed(item);
    }
  };

  // Confirm removal and return kit to predicted pool (REQ-KIT-015)
  const confirmRemoveUsed = (item: UsedKitItem): void => {
    setUsedKits((prev) => prev.filter((u) => u.id !== item.id));
    setPredictedKits((prev) => {
      if (prev.some((p) => p.labKitId === item.labKitId)) {
        return prev;
      }
      return [
        ...prev,
        {
          id: item.labKitId,
          labKitId: item.labKitId,
          name: item.name,
          count: 1,
          notes: null,
          expiration: item.expiration,
        },
      ];
    });
    setRemovalTarget(null);
  };

  // Change expiry date for a used kit
  const handleDateChange = (item: UsedKitItem, newDate: string): void => {
    if (isDateExpired(newDate)) {
      setExpiredWarning({ item, date: newDate });
    }
    setUsedKits((prev) =>
      prev.map((u) => (u.id === item.id ? { ...u, expirationDate: newDate } : u)),
    );
  };

  // Change status (przygotowany vs zużyty)
  const handleStatusToggle = (item: UsedKitItem): void => {
    setUsedKits((prev) =>
      prev.map((u) =>
        u.id === item.id
          ? { ...u, status: u.status === 'przygotowany' ? 'zużyty' : 'przygotowany' }
          : u,
      ),
    );
  };

  // Automatically recalculate flag (REQ-KIT-022, #auto-flag)
  const handleAutoFlag = (): void => {
    if (usedKits.length === 0) {
      setFlag(PATIENT_VISIT_FLAGS.WHITE);
      return;
    }
    // Check if predicted list is empty (all kits moved) and all date-bearing kits have an expiry selected
    const allMoved = predictedKits.length === 0;
    const allDatesSelected = usedKits.every((u) => !u.expiration || Boolean(u.expirationDate));

    if (allMoved && allDatesSelected) {
      setFlag(PATIENT_VISIT_FLAGS.GREEN);
    } else {
      setFlag(PATIENT_VISIT_FLAGS.RED);
    }
  };

  // Save kit registration (REQ-KIT-013..022): kits + flag in one API call
  const queryClient = useQueryClient();
  const saveMutation = useMutation({
    mutationFn: async () => {
      const items: SaveVisitKitsRequest['items'] = [];
      for (const used of usedKits) {
        let labKitExpirationId: number | null = null;
        if (used.expiration) {
          const batches = await loadExpirations(used.labKitId);
          const batch = batches.find((b) => b.expirationDate === used.expirationDate);
          if (!batch) {
            throw new Error(`Wybierz datę przydatności dla kitu „${used.name}”`);
          }
          labKitExpirationId = batch.id;
        }
        items.push({ labKitId: used.labKitId, labKitExpirationId, status: used.status });
      }
      return kitsApi.saveVisitKits(patientVisitId, { flag, items });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: visitQueryKeys.all });
      void queryClient.invalidateQueries({ queryKey: patientQueryKeys.all });
      void queryClient.invalidateQueries({ queryKey: kitQueryKeys.stock.all });
      onSaved?.(flag, usedKits);
      onClose();
    },
  });

  const handleSave = (): void => {
    saveMutation.mutate();
  };

  if (!canRead) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-xs">
        <div className="w-full max-w-md rounded-lg border border-slate-800 bg-slate-900 p-6 text-slate-100 shadow-xl">
          <h3 className="text-base font-semibold text-rose-400">Brak dostępu</h3>
          <p className="mt-2 text-xs text-slate-300">
            Nie masz uprawnień do rejestracji kitów laboratoryjnych. Jeżeli uważasz, że to błąd,
            skontaktuj się z administratorem ośrodka.
          </p>
          <div className="mt-4 flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-800"
            >
              Zamknij
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Rejestracja kitów laboratoryjnych"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4 backdrop-blur-xs"
    >
      <div className="flex h-[90vh] max-h-[860px] w-full max-w-[1340px] flex-col rounded-xl border border-slate-800 bg-slate-900 text-slate-100 shadow-2xl">
        {/* HEADER (REQ-KIT-024, REQ-KIT-021) */}
        <header className="flex items-center justify-between border-b border-slate-800 px-6 py-4">
          <div className="flex items-center gap-4">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-cyan-950 text-cyan-400 ring-1 ring-cyan-800">
              <Package className="h-5 w-5" />
            </div>
            <div>
              <div className="flex items-center gap-3">
                <h2 className="text-base font-bold text-slate-100">
                  Rejestracja kitów laboratoryjnych
                </h2>
                {/* Kit flag badge */}
                <span
                  data-testid="header-kit-flag-badge"
                  className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium ${KIT_FLAG_COLORS[flag].bg} ${KIT_FLAG_COLORS[flag].text} ${KIT_FLAG_COLORS[flag].border}`}
                >
                  <span className="h-1.5 w-1.5 rounded-full bg-current" />
                  Flaga: {flag === 'W' ? 'Biała' : flag === 'R' ? 'Czerwona' : 'Zielona'}
                </span>
              </div>
              <div className="mt-1 flex items-center gap-4 text-xs text-slate-400">
                <span>
                  Pacjent: <strong className="text-slate-200">{patientName}</strong> ·{' '}
                  <span className="font-mono text-cyan-300">{patientCode}</span>
                </span>
                <span>•</span>
                <span>
                  Wizyta: <strong className="text-slate-200">{visitLabel}</strong> · Badanie{' '}
                  <span className="font-medium text-slate-300">{trialShortcut}</span>
                </span>
              </div>
            </div>
          </div>

          <button
            type="button"
            id="close-kit-registration"
            data-testid="close-kit-registration"
            onClick={onClose}
            aria-label="Zamknij rejestrację kitów"
            className="rounded-md border border-slate-700 bg-slate-850 p-2 text-slate-400 transition-colors hover:bg-slate-800 hover:text-slate-200"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        {/* BODY — TWO PANELS (REQ-KIT-017) */}
        <div className="grid flex-1 grid-cols-2 gap-4 overflow-hidden p-6">
          {/* LEFT PANEL: PREDICTED KITS */}
          <div className="flex flex-col rounded-lg border border-slate-800 bg-slate-950/60 p-4">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <div>
                <h3 className="text-sm font-semibold text-slate-200">
                  {source === 'visit' ? 'Kity przewidziane na wizytę' : 'Wszystkie kity badania'}
                </h3>
                <span className="text-[11px] text-slate-400">
                  {predictedKits.length} pozycji do dyspozycji
                </span>
              </div>

              {/* Source radio filter (REQ-KIT-020) */}
              <div className="flex items-center gap-3 text-xs text-slate-300">
                <label className="flex cursor-pointer items-center gap-1.5">
                  <input
                    type="radio"
                    name="kit-source"
                    data-testid="source-visit"
                    checked={source === 'visit'}
                    onChange={() => setSource('visit')}
                    className="accent-cyan-500"
                  />
                  <span>Tylko ta wizyta</span>
                </label>
                <label className="flex cursor-pointer items-center gap-1.5">
                  <input
                    type="radio"
                    name="kit-source"
                    data-testid="source-all-trial"
                    checked={source === 'trial'}
                    onChange={() => setSource('trial')}
                    className="accent-cyan-500"
                  />
                  <span>Wszystkie kity badania</span>
                </label>
              </div>
            </div>

            {/* List / Table */}
            <div className="flex-1 overflow-y-auto py-2">
              {predictedKits.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center p-6 text-center text-slate-400">
                  <Package className="h-8 w-8 text-slate-600 mb-2" />
                  <p className="text-xs font-medium text-slate-300">
                    Brak kitów przewidzianych na wizytę
                  </p>
                  <p className="mt-1 text-[11px] text-slate-500 max-w-xs">
                    Wszystkie kity zostały przeniesione na listę użytych lub protokół nie definiuje kitów dla tej wizyty.
                  </p>
                  <div className="mt-4 flex gap-2">
                    <button
                      type="button"
                      id="show-all-kits-empty"
                      data-testid="show-all-kits-empty"
                      onClick={() => setSource('trial')}
                      className="rounded border border-cyan-800 bg-cyan-950/40 px-2.5 py-1 text-xs text-cyan-300 hover:bg-cyan-900/60"
                    >
                      Pokaż wszystkie kity badania
                    </button>
                    <button
                      type="button"
                      id="open-kit-assignment-empty"
                      data-testid="open-kit-assignment-empty"
                      onClick={() => setInfoModal('Otwarto moduł przypisań kitów do pozycji protokołu (REQ-KIT-012).')}
                      className="rounded border border-slate-700 px-2.5 py-1 text-xs text-slate-300 hover:bg-slate-800"
                    >
                      Przypisania kitów
                    </button>
                  </div>
                </div>
              ) : (
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="border-b border-slate-800/80 text-slate-400">
                      <th scope="col" className="px-3 py-2">Kit</th>
                      <th scope="col" className="px-3 py-2 text-center">Ilość</th>
                      <th scope="col" className="px-3 py-2">Uwagi</th>
                      <th scope="col" className="px-3 py-2 text-right">Akcja</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-850">
                    {predictedKits.map((kit) => (
                      <tr key={kit.id} className="hover:bg-slate-900/50">
                        <td className="px-3 py-2.5">
                          <span className="font-medium text-slate-200">{kit.name}</span>
                          {!kit.expiration && (
                            <span className="ml-2 rounded bg-slate-800 px-1.5 py-0.2 text-[10px] text-slate-400">
                              bezterminowy
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-center font-mono text-slate-300">
                          {kit.count}
                        </td>
                        <td className="px-3 py-2.5 text-slate-400">
                          {kit.notes ?? '—'}
                        </td>
                        <td className="px-3 py-2.5 text-right">
                          <button
                            type="button"
                            data-testid={`move-kit-${kit.labKitId}`}
                            onClick={() => void handleMoveToUsed(kit)}
                            title="Przenieś kit na listę użytych"
                            className="inline-flex items-center gap-1 rounded border border-cyan-700/80 bg-cyan-950/60 px-2 py-1 text-[11px] font-medium text-cyan-300 hover:bg-cyan-900"
                          >
                            <span>Przenieś</span>
                            <ArrowRight className="h-3 w-3" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Bottom move all button */}
            {predictedKits.length > 0 && (
              <div className="border-t border-slate-800 pt-3">
                <button
                  type="button"
                  id="move-all-to-used"
                  data-testid="move-all-to-used"
                  onClick={() => void handleMoveAllToUsed()}
                  className="flex w-full items-center justify-center gap-2 rounded-md border border-cyan-800/80 bg-cyan-950/40 px-3 py-2 text-xs font-semibold text-cyan-300 hover:bg-cyan-900/60 transition-colors"
                >
                  <span>Przenieś wszystkie →</span>
                </button>
              </div>
            )}
          </div>

          {/* RIGHT PANEL: USED / PREPARED KITS (REQ-KIT-017..019) */}
          <div className="flex flex-col rounded-lg border border-slate-800 bg-slate-950/60 p-4">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <div>
                <h3 className="text-sm font-semibold text-slate-200">Kity użyte / przygotowane</h3>
                <span className="text-[11px] text-slate-400">
                  {usedKits.length} pozycji zarejestrowanych
                </span>
              </div>
              <span className="text-[11px] text-cyan-400/90 font-medium">
                Sugestia FEFO aktywna
              </span>
            </div>

            {/* List / Table */}
            <div className="flex-1 overflow-y-auto py-2">
              {usedKits.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center p-6 text-center text-slate-500">
                  <Package className="h-8 w-8 text-slate-700 mb-2" />
                  <p className="text-xs">Brak użytych lub przygotowanych kitów</p>
                  <p className="mt-1 text-[11px] text-slate-600 max-w-xs">
                    Przeciągnij kit z lewego panelu tutaj lub użyj przycisku „Przenieś”.
                  </p>
                </div>
              ) : (
                <table className="w-full text-left text-xs">
                  <thead>
                    <tr className="border-b border-slate-800/80 text-slate-400">
                      <th scope="col" className="px-3 py-2">Kit</th>
                      <th scope="col" className="px-3 py-2">Data przydatności</th>
                      <th scope="col" className="px-3 py-2">Status</th>
                      <th scope="col" className="px-3 py-2 text-right">Akcja</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-850">
                    {usedKits.map((item, idx) => {
                      const expList = expirationsCache[item.labKitId] ?? [];
                      const isExpired = item.expirationDate ? isDateExpired(item.expirationDate) : false;

                      return (
                        <tr key={item.id} className="hover:bg-slate-900/50">
                          <td className="px-3 py-2.5">
                            <span className="font-medium text-slate-200">{item.name}</span>
                          </td>

                          {/* Expiration date picker with FEFO note (REQ-KIT-018, REQ-KIT-019) */}
                          <td className="px-3 py-2.5">
                            {!item.expiration ? (
                              <span className="text-slate-400">— (bezterminowy)</span>
                            ) : expList.length === 0 && !item.expirationDate ? (
                              <div className="flex items-center gap-1.5 text-amber-400">
                                <span className="rounded bg-amber-950/60 px-1.5 py-0.5 text-[10px] ring-1 ring-amber-800">
                                  ? brak partii
                                </span>
                                <button
                                  type="button"
                                  id={`pick-expiry-${idx + 1}`}
                                  data-testid={`pick-expiry-${idx + 1}`}
                                  onClick={() => void loadExpirations(item.labKitId)}
                                  className="text-[11px] underline hover:text-amber-300"
                                >
                                  Sprawdź daty
                                </button>
                              </div>
                            ) : (
                              <div className="flex flex-col gap-1">
                                <select
                                  data-testid={`select-expiry-${item.id}`}
                                  value={item.expirationDate ?? ''}
                                  onChange={(e) => handleDateChange(item, e.target.value)}
                                  className={`rounded border bg-slate-900 px-2 py-1 text-xs focus:outline-none ${
                                    isExpired
                                      ? 'border-amber-600 text-amber-300 ring-1 ring-amber-700'
                                      : 'border-slate-700 text-slate-200 focus:border-cyan-500'
                                  }`}
                                >
                                  {expList.length === 0 && (
                                    <option value={item.expirationDate ?? ''}>
                                      {item.expirationDate ? `${item.expirationDate} (FEFO)` : '— wybierz partię —'}
                                    </option>
                                  )}
                                  {expList.map((exp, expIdx) => (
                                    <option key={exp.id} value={exp.expirationDate}>
                                      {exp.expirationDate} {expIdx === 0 ? '(FEFO)' : ''}
                                    </option>
                                  ))}
                                </select>

                                {isExpired && (
                                  <span
                                    data-testid="expired-badge"
                                    className="flex items-center gap-1 text-[10px] font-medium text-amber-400"
                                  >
                                    <AlertTriangle className="h-3 w-3" />
                                    <span>! przeterminowany (REQ-KIT-026)</span>
                                  </span>
                                )}
                              </div>
                            )}
                          </td>

                          {/* Status toggle: przygotowany vs zużyty */}
                          <td className="px-3 py-2.5">
                            <button
                              type="button"
                              onClick={() => handleStatusToggle(item)}
                              title="Kliknij, aby zmienić status przygotowany / zużyty"
                              className={`rounded-full border px-2.5 py-0.5 text-[10px] font-medium transition-colors ${
                                item.status === 'przygotowany'
                                  ? 'border-cyan-800 bg-cyan-950/70 text-cyan-300 hover:bg-cyan-900/60'
                                  : 'border-emerald-800 bg-emerald-950/70 text-emerald-300 hover:bg-emerald-900/60'
                              }`}
                            >
                              {item.status === 'przygotowany' ? 'przygotowany' : 'zużyty'}
                            </button>
                          </td>

                          {/* Delete action */}
                          <td className="px-3 py-2.5 text-right">
                            <button
                              type="button"
                              id={`remove-used-row-${idx + 1}`}
                              data-testid={`remove-used-row-${idx + 1}`}
                              onClick={() => handleRequestRemoveUsed(item)}
                              title="Usuń pozycję z użytych i przywróć na stan"
                              className="rounded p-1 text-slate-400 hover:bg-slate-800 hover:text-rose-300"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>

            {/* Helper note (REQ-KIT-019) */}
            <div className="border-t border-slate-800 pt-3 text-[11px] text-slate-400">
              <span className="font-semibold text-slate-300">Sugestia FEFO:</span> najpierw partie z
              najkrótszą datą ważności (można wybrać inną partię z listy).
            </div>
          </div>
        </div>

        {/* FOOTER (REQ-KIT-021, REQ-KIT-022, REQ-KIT-023) */}
        <footer className="flex items-center justify-between border-t border-slate-800 bg-slate-950 px-6 py-4">
          {/* Flag selector + Auto button */}
          <div className="flex items-center gap-4">
            <span className="text-xs font-semibold uppercase tracking-wider text-slate-300">
              Flaga rejestracji kitów:
            </span>

            <div className="flex items-center gap-3 text-xs">
              <label className="flex cursor-pointer items-center gap-1.5 text-slate-200">
                <input
                  type="radio"
                  name="kit-flag"
                  data-testid="flag-white"
                  checked={flag === PATIENT_VISIT_FLAGS.WHITE}
                  onChange={() => setFlag(PATIENT_VISIT_FLAGS.WHITE)}
                  className="accent-slate-400"
                />
                <span>Biała — brak użycia</span>
              </label>

              <label className="flex cursor-pointer items-center gap-1.5 text-rose-300">
                <input
                  type="radio"
                  name="kit-flag"
                  data-testid="flag-red"
                  checked={flag === PATIENT_VISIT_FLAGS.RED}
                  onChange={() => setFlag(PATIENT_VISIT_FLAGS.RED)}
                  className="accent-rose-500"
                />
                <span>Czerwona — niepełny</span>
              </label>

              <label className="flex cursor-pointer items-center gap-1.5 text-emerald-300">
                <input
                  type="radio"
                  name="kit-flag"
                  data-testid="flag-green"
                  checked={flag === PATIENT_VISIT_FLAGS.GREEN}
                  onChange={() => setFlag(PATIENT_VISIT_FLAGS.GREEN)}
                  className="accent-emerald-500"
                />
                <span>Zielona — komplet</span>
              </label>
            </div>

            <button
              type="button"
              id="auto-flag"
              data-testid="auto-flag"
              onClick={handleAutoFlag}
              title="Oblicz flagę automatycznie na podstawie kompletu"
              className="inline-flex items-center gap-1.5 rounded-md border border-cyan-800 bg-cyan-950/60 px-2.5 py-1 text-xs font-medium text-cyan-300 hover:bg-cyan-900"
            >
              <Sparkles className="h-3 w-3" />
              <span>Ustaw automatycznie</span>
            </button>
          </div>

          {/* Quick link buttons & Save */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              id="open-kit-assignment"
              data-testid="open-kit-assignment"
              onClick={() => setInfoModal('Otwarto moduł przypisań kitów do wizyty protokołu (REQ-KIT-012).')}
              className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-800"
            >
              Przypisania kitów
            </button>

            <button
              type="button"
              id="open-lab-module"
              data-testid="open-lab-module"
              onClick={() => {
                if (onOpenInventory) {
                  onOpenInventory();
                } else {
                  setInfoModal('Przejście do modułu laboratoryjnego (REQ-KIT-023). Magazyn jest w przygotowaniu.');
                }
              }}
              className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-800"
            >
              Moduł laboratoryjny
            </button>

            {saveMutation.isError && (
              <p role="alert" data-testid="save-kit-error" className="text-xs text-rose-400">
                {saveMutation.error instanceof Error
                  ? saveMutation.error.message
                  : 'Nie udało się zapisać rejestracji kitów'}
              </p>
            )}
            <button
              type="button"
              id="save-kit-registration"
              data-testid="save-kit-registration"
              disabled={!canWrite || saveMutation.isPending}
              aria-busy={saveMutation.isPending}
              onClick={handleSave}
              className="inline-flex items-center gap-2 rounded-md bg-cyan-600 px-4 py-1.5 text-xs font-semibold text-white shadow-xs hover:bg-cyan-500 disabled:opacity-50"
            >
              {saveMutation.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Check className="h-4 w-4" />
              )}
              <span>{saveMutation.isPending ? 'Zapisywanie…' : 'Zapisz rejestrację kitów'}</span>
            </button>
          </div>
        </footer>

        {/* MODAL: Potwierdzenie — usunięcie przygotowania (REQ-KIT-015) */}
        {removalTarget && (
          <div
            role="alertdialog"
            aria-modal="true"
            aria-label="Przywrócić kit na stan?"
            className="fixed inset-0 z-60 flex items-center justify-center bg-black/80 p-4"
          >
            <div className="w-full max-w-md rounded-lg border border-slate-800 bg-slate-900 p-6 text-slate-100 shadow-xl">
              <h3 className="text-base font-semibold text-amber-400">Przywrócić kit na stan?</h3>
              <p className="mt-2 text-xs text-slate-300">
                Kit <strong className="text-slate-100">{removalTarget.name}</strong>{' '}
                {removalTarget.expirationDate ? `(data ${removalTarget.expirationDate})` : ''} jest tylko
                przygotowany (niezużyty fizycznie). Usunięcie wróci go do puli dostępnej.
              </p>
              <div className="mt-5 flex justify-end gap-3">
                <button
                  type="button"
                  id="cancel-remove-prep"
                  data-testid="cancel-remove-prep"
                  onClick={() => setRemovalTarget(null)}
                  className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-800"
                >
                  Anuluj
                </button>
                <button
                  type="button"
                  id="confirm-remove-prep"
                  data-testid="confirm-remove-prep"
                  onClick={() => confirmRemoveUsed(removalTarget)}
                  className="rounded-md bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-500"
                >
                  Usuń przygotowanie
                </button>
              </div>
            </div>
          </div>
        )}

        {/* MODAL: Ostrzeżenie — kit przeterminowany (REQ-KIT-026) */}
        {expiredWarning && (
          <div
            role="alertdialog"
            aria-modal="true"
            aria-label="Zarejestrować kit przeterminowany?"
            className="fixed inset-0 z-60 flex items-center justify-center bg-black/80 p-4"
          >
            <div className="w-full max-w-md rounded-lg border border-amber-800/80 bg-slate-900 p-6 text-slate-100 shadow-xl">
              <div className="flex items-center gap-2 text-amber-400">
                <AlertTriangle className="h-5 w-5" />
                <h3 className="text-base font-semibold">Zarejestrować kit przeterminowany?</h3>
              </div>
              <p className="mt-2 text-xs text-slate-300">
                Wybrana partia <strong className="text-slate-100">{expiredWarning.item.name}</strong> ma
                datę ważności <strong className="font-mono text-amber-300">{expiredWarning.date}</strong>{' '}
                (przeterminowana). System nie blokuje zapisu — zarejestruj zgodnie z faktycznym zużyciem.
              </p>
              <div className="mt-5 flex justify-end gap-3">
                <button
                  type="button"
                  id="cancel-expired-use"
                  data-testid="cancel-expired-use"
                  onClick={() => setExpiredWarning(null)}
                  className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-800"
                >
                  Anuluj
                </button>
                <button
                  type="button"
                  id="confirm-expired-use"
                  data-testid="confirm-expired-use"
                  onClick={() => setExpiredWarning(null)}
                  className="rounded-md bg-amber-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-500"
                >
                  Zarejestruj mimo to
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Informational notification modal */}
        {infoModal && (
          <div
            role="dialog"
            aria-modal="true"
            className="fixed inset-0 z-60 flex items-center justify-center bg-black/70 p-4"
          >
            <div className="w-full max-w-sm rounded-lg border border-slate-800 bg-slate-900 p-5 text-slate-100 shadow-lg">
              <h4 className="text-sm font-semibold text-cyan-400">Informacja systemowa</h4>
              <p className="mt-2 text-xs text-slate-300">{infoModal}</p>
              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={() => setInfoModal(null)}
                  className="rounded-md border border-slate-700 px-3 py-1 text-xs text-slate-300 hover:bg-slate-800"
                >
                  OK
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
